import { isNilOrEmpty } from "@utils/index"
import * as R from "ramda"
import React from "react"

import {
  hasBrand,
  hasColor,
  hasDiscounted,
  hasDiscountTier,
  hasSeries,
  hasStock,
  hasSubType,
} from "../Products/utils"

type Args = {
  availablePrintedDesignVariants: string[]
  filters: ProductsFilters
  formattedProducts: any[]
  notAvailableInCustomerCountry: string[]
}

type ProductsFilters = {
  brandFilters: any[]
  colorFilters: any[]
  devicesFilter: any[]
  discountTierFilters: any[]
  seriesFilters: any[]
  showBundleProducts: boolean
  showClearance: boolean
  showInStock: boolean
  showMagnetCompatibleProducts: boolean
  showPrintedDesigns: boolean
  subTypeFilters: any[]
}

/**
 * Filters products for the Products view based on active filters
 */
export function useFilteredProducts({
  availablePrintedDesignVariants,
  filters,
  formattedProducts,
  notAvailableInCustomerCountry,
}: Args) {
  const {
    brandFilters,
    colorFilters,
    devicesFilter,
    discountTierFilters,
    seriesFilters,
    showBundleProducts,
    showClearance,
    showInStock,
    showMagnetCompatibleProducts,
    showPrintedDesigns,
    subTypeFilters,
  } = filters

  const filterByBundleProducts = R.filter((p: any) => !!p.bundleProduct)
  const filterByMagnetCompatible = R.filter((p: any) =>
    Boolean(p.builtInMagnet)
  )
  const filterBySubType = R.filter((p) => hasSubType(p, subTypeFilters))
  const filterByColor = R.filter((p) => hasColor(p, colorFilters))
  const filterByBrand = R.filter((p) => hasBrand(p, brandFilters))
  const filterBySeries = R.filter((p) => hasSeries(p, seriesFilters))
  const filterByDiscountTier = R.filter((p) =>
    hasDiscountTier(p, discountTierFilters)
  )
  const filterByDevice = R.filter((p: any) => {
    return !!p?.variants?.find(
      ({ deviceName }) => deviceName === devicesFilter[0].label
    )
  })

  const filteredProducts = React.useMemo(() => {
    const withoutProhibitedProducts = formattedProducts.filter(
      (product) => !notAvailableInCustomerCountry.includes(product.slug)
    )

    const [allProducts, availablePrintedDesignProducts] =
      withoutProhibitedProducts.reduce(
        (result, product) => {
          if (product.customPrint) {
            const availableVariants: any = []
            product.variants.forEach((variant) => {
              if (availablePrintedDesignVariants.includes(variant.sku)) {
                availableVariants.push(variant)
              }
            })

            if (availableVariants.length > 0) {
              result[0].push(product)
              result[1].push({
                ...product,
                variants: availableVariants,
              })
            } else {
              result[2].push(product)
            }
          } else {
            result[0].push(product)
          }

          return result
        },
        [[], [], []]
      )

    if (
      showPrintedDesigns &&
      !subTypeFilters.length &&
      !colorFilters.length &&
      !seriesFilters.length &&
      !showClearance &&
      !showInStock
    ) {
      return availablePrintedDesignProducts
    }

    const shownPrintedDesigns = showPrintedDesigns
      ? availablePrintedDesignProducts
      : []

    return [
      ...shownPrintedDesigns,
      ...R.pipe(
        R.unless(() => !showBundleProducts, filterByBundleProducts),
        R.unless(() => !showMagnetCompatibleProducts, filterByMagnetCompatible),
        R.unless(() => isNilOrEmpty(subTypeFilters), filterBySubType),
        R.unless(() => isNilOrEmpty(colorFilters), filterByColor),
        R.unless(() => isNilOrEmpty(brandFilters), filterByBrand),
        R.unless(() => isNilOrEmpty(seriesFilters), filterBySeries),
        R.unless(() => isNilOrEmpty(discountTierFilters), filterByDiscountTier),
        R.unless(() => isNilOrEmpty(devicesFilter), filterByDevice),
        R.unless(() => !showClearance, R.filter(hasDiscounted)),
        R.unless(() => !showInStock, R.filter(hasStock))
      )(allProducts),
    ]
  }, [
    formattedProducts,
    notAvailableInCustomerCountry,
    availablePrintedDesignVariants,
    showPrintedDesigns,
    showBundleProducts,
    showMagnetCompatibleProducts,
    subTypeFilters,
    colorFilters,
    brandFilters,
    seriesFilters,
    discountTierFilters,
    devicesFilter,
    showClearance,
    showInStock,
  ])

  return filteredProducts
}
